import * as Sentry from "@sentry/astro";

// Init del SDK en el navegador. @sentry/astro 7 solo traspasa dsn, environment,
// release y sampleRate desde astro.config.mjs al init que genera; ignoreErrors,
// denyUrls y beforeSend se ignoraban en silencio, por eso viven acá. El
// archivo se inyecta solo cuando la integración está `enabled` (producción).

declare const __SENTRY_DSN__: string;

type EventoSentry = Parameters<
  NonNullable<Parameters<typeof Sentry.init>[0]["beforeSend"]>
>[0];

/** Fallos de import() de un chunk con hash viejo (tras un deploy). */
const ERRORES_CHUNK = [
  /Failed to fetch dynamically imported module/,
  /Importing a module script failed/,
  /error loading dynamically imported module/,
];

/** Mensajes de fetch/XHR que dependen de la red del cliente, no del código. */
const ERRORES_RED = [
  "Failed to fetch",
  "Load failed",
  "NetworkError when attempting to fetch resource.",
  "Network request failed",
];

const textoExcepcion = (event: EventoSentry): string => {
  const valores = event.exception?.values ?? [];
  const partes = valores.map((v) => `${v.type ?? ""}: ${v.value ?? ""}`);
  if (event.message) partes.push(event.message);
  return partes.join(" | ");
};

/** Hay al menos un frame de nuestro bundle (los assets de Astro van en /_astro/). */
const tieneFrameNuestro = (event: EventoSentry): boolean =>
  (event.exception?.values ?? []).some((v) =>
    (v.stacktrace?.frames ?? []).some((f) =>
      (f.filename ?? "").includes("/_astro/"),
    ),
  );

/** Layout.astro marca esto cuando ya recargó por un chunk caído y volvió a fallar. */
const recargaDeChunkFallo = (): boolean => {
  try {
    return sessionStorage.getItem("chunk-reload-fallo") === "1";
  } catch {
    return false;
  }
};

Sentry.init({
  dsn: __SENTRY_DSN__,
  environment: import.meta.env.PUBLIC_APP_ENV,
  sampleRate: 0.6, // muestreo de errores (excepciones)
  tracesSampleRate: 0.1, // muestreo de transacciones (requests)
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  replaysSessionSampleRate: 0.2,
  replaysOnErrorSampleRate: 0.8,

  // Errores de terceros o del navegador que no podemos arreglar. Cada patrón
  // corresponde a issues concretos de Sentry; no se silencian categorías.
  ignoreErrors: [
    // Navegadores embebidos de Facebook/Instagram en iOS inyectan un handler
    // de autocompletado que no existe (FREST-ASTRO-M, 5, EN, 38, MN).
    "_AutofillCallbackHandler",
    // WebViews de iOS sin el puente nativo esperado por scripts de la app
    // contenedora (FREST-ASTRO-TA, TS, V8).
    "window.webkit.messageHandlers",
    // WebViews de Android (iabjs://) cuando la app contenedora destruye el
    // objeto Java del puente (FREST-ASTRO-TE, V2, TD).
    "Java object is gone",
    "Java exception was raised during method invocation",
    // Escáner de enlaces de Outlook (SafeLinks) ejecutando la página en un
    // entorno sin DOM real (FREST-ASTRO-V5).
    "Object Not Found Matching Id",
    // Aviso benigno del navegador cuando un ResizeObserver no alcanza a
    // entregar notificaciones en un frame; no rompe nada (FREST-ASTRO-3).
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
    // Error interno de Firefox sin stack ni contexto (FREST-ASTRO-SJ).
    "NS_ERROR_FAILURE",
    // Iframes cross-origin (Google Maps, tag de Pinterest, Replay) a los que el
    // navegador bloquea el acceso (FREST-ASTRO-TJ, V3).
    "Blocked a frame with origin",
    // Google Maps bloqueado por adblock o red: la app degrada sin mapa y la
    // validación de zonas ya no depende de él (FREST-ASTRO-TP, TZ, T5, SZ).
    "The Google Maps JavaScript API could not load.",
    'Could not load "map".',
    // @egjs/flicking: el usuario tocó el carrusel durante una animación de
    // autoplay; es el flujo normal de la librería (FREST-ASTRO-RQ).
    "Animation is interrupted by user input.",
  ],

  // Scripts de terceros: sus errores no son accionables desde este repo.
  denyUrls: [
    /ct\.pinterest\.com/, // tag de Pinterest (FREST-ASTRO-Z, TK)
    /connect\.facebook\.net/, // pixel de Meta
    /googletagmanager\.com/, // gtag / GTM
    /hotjar\.com/, // Hotjar
    /analytics\.tiktok\.com/, // pixel de TikTok
    /^chrome-extension:\/\//,
    /^moz-extension:\/\//,
    /^safari(-web)?-extension:\/\//,
  ],

  beforeSend(event) {
    const texto = textoExcepcion(event);

    // Chunks caídos tras un deploy: Layout.astro recarga la página una vez
    // (1/min). Solo interesa cuando la recarga ya se hizo y sigue fallando.
    if (ERRORES_CHUNK.some((patron) => patron.test(texto))) {
      if (!recargaDeChunkFallo()) return null;
      event.tags = { ...event.tags, recarga_chunk: "fallida" };
      return event;
    }

    // Red del cliente sin ningún frame de nuestro código: no hay nada que
    // arreglar (FREST-ASTRO-S, Z, 1F, BR). Si el fallo viene de un fetch
    // nuestro el frame de /_astro/ está y el evento pasa igual.
    if (
      ERRORES_RED.some((mensaje) => texto.includes(mensaje)) &&
      !tieneFrameNuestro(event)
    ) {
      return null;
    }

    return event;
  },
});
